import { useEffect, useState } from 'react'
import { useCombobox } from 'downshift'
import { useFormikContext } from 'formik'
import { useDebounceValue } from 'utils'
import {
  InvitableUserInfoFragment,
  useGetShareInvitableUsersQuery,
} from 'gqlTypes'
import { useToast } from 'containers/ToastManager'

interface AutocompleteProps {
  shareIdentifier: string
}

interface Values {
  shouldValidateEmail: boolean
  email: string
  userIdentifier: string | null
}

/**
 * Hook to orchestrate all the logic to create a users autocomplete
 */
const useAutocomplete = ({ shareIdentifier }: AutocompleteProps) => {
  const { showToast } = useToast()
  const [items, setItems] = useState<InvitableUserInfoFragment[]>([])

  const formik = useFormikContext<Values>()

  const {
    setFieldValue,
    validateForm,
    values: { email },
  } = formik

  const combobox = useCombobox({
    items,
    itemToString: item => item?.name || '',
  })

  const { inputValue, selectedItem, reset } = combobox
  const debouncedValue = useDebounceValue(inputValue, 300)
  const isValueSelected = selectedItem?.name === inputValue
  const isValueSelectedDebounced = selectedItem?.name === debouncedValue
  const isValueAnEmail = debouncedValue.includes('@')
  const isValueTooShort = debouncedValue.length === 0
  const isValueEmpty = inputValue.trim() === ''
  const skip =
    isValueAnEmail ||
    isValueTooShort ||
    isValueSelectedDebounced ||
    isValueEmpty

  const { loading } = useGetShareInvitableUsersQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      limit: 5,
      shareId: shareIdentifier,
      search: debouncedValue,
    },
    skip,
    onCompleted(data) {
      setItems(data?.share?.invitableUsers?.entries ?? [])
    },
    onError() {
      showToast('Failed to autocomplete name.', 'negative')
    },
  })

  // If we're skipping the query, then clear the items
  useEffect(() => {
    if (skip) {
      setItems([])
    }
  }, [skip])

  // Reset combobox if Formik form is reset
  useEffect(() => {
    if (email === '') {
      reset()
    }
  }, [email, reset])

  // Validate values on change to enable/disable
  // submit button accordingly
  // Need to use this due to bug in Formik:
  // https://github.com/formium/formik/issues/2083#issuecomment-734628995
  useEffect(() => {
    validateForm(formik.values)
  }, [validateForm, formik.values])

  useEffect(() => {
    // When the field value does not match the combobox selection
    // then we indicate to formik we should be treating it as a plain email value
    setFieldValue('shouldValidateEmail', !isValueSelectedDebounced)
    // Set the userIdentifier if we are using value from the autocomplete. Otherwise, we will
    // send the plain email
    setFieldValue(
      'userIdentifier',
      isValueSelectedDebounced && selectedItem?.identifier
    )
    // Set the formik value used to make the mutation to either combobox selection
    // or the input plain value, depending on whether a combobox item has been selected
    setFieldValue(
      'email',
      isValueSelectedDebounced ? selectedItem?.email : inputValue
    )
  }, [isValueSelectedDebounced, inputValue, selectedItem, setFieldValue])

  return {
    loading,
    items,
    formik,
    combobox,
    isValueSelected,
  }
}

export { useAutocomplete }
